import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "../Button/Button";
import styles from "./Modal.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * How wide the dialog is allowed to get. Omitting it keeps the 400px every
   * modal written before this prop was designed against, so widening one is
   * always an explicit decision at the call site rather than a global shift.
   */
  width?: ModalWidth;
  /**
   * A glyph for the header, drawn in a 34px tile to the left of the title.
   *
   * Opt-in rather than derived from anything: a dialog that asks a question
   * needs no picture of itself, and most of ours don't have one. The ones that
   * do are the dialogs an operator should recognise before reading — the
   * destructive confirmations — so the tile is a decision made per call site,
   * not a consequence of some other prop.
   *
   * The tile's geometry is the header's business and lives here; only its
   * colour is the caller's, through `iconTone`.
   */
  icon?: ReactNode;
  /**
   * The tile's colouring. Neutral by default, so a caller who just wants a
   * glyph in the header does not get a red one: `danger` is the alarm, and an
   * alarm nobody chose is not an alarm.
   *
   * `success` exists for the dialogs that stay open past the operation they
   * asked about — the wipe reports its own outcome in the window that started
   * it, and a red tile over "Project wiped" would say the opposite of the
   * sentence beside it.
   */
  iconTone?: ModalIconTone;
  /**
   * Takes every exit away while a request the dialog started is in flight: the
   * × greys out and stops responding, Escape does nothing, and a click on the
   * backdrop is ignored.
   *
   * A caller could pass a no-op onClose instead, and the dialog would behave
   * the same — but the × would still look live, and an operator pressing it in
   * the tensest second of a destructive operation would get silence. Refusing
   * and LOOKING refused are two different things, and only the second one is
   * an answer.
   */
  closeDisabled?: boolean;
}

type ModalWidth = "sm" | "lg";
type ModalIconTone = "neutral" | "danger" | "success";

const WIDTH_CLASS: Record<ModalWidth, string> = {
  sm: styles.widthSm,
  lg: styles.widthLg,
};

const ICON_TONE_CLASS: Record<ModalIconTone, string> = {
  neutral: styles.iconNeutral,
  danger: styles.iconDanger,
  success: styles.iconSuccess,
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  icon,
  iconTone = "neutral",
  closeDisabled = false,
}: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  /**
   * `onClose` reached through a ref rather than through the effect's
   * dependencies. Opening a dialog is what places the focus, so the effect
   * below must run when `open` changes and at no other time — with the handler
   * in the deps it re-ran on every render that passed a fresh closure, which is
   * every render of a caller that writes `onClose={() => setThing(null)}`, and
   * it took the operator's focus with it each time. Making that correct at
   * every call site is a rule nobody can keep; not depending on it is one
   * change here.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Read through a ref for the same reason as onClose: the key handler is
  // installed once per open, and it has to see the CURRENT answer to "may this
  // dialog be closed", not the one that was true when it was installed.
  const closeDisabledRef = useRef(closeDisabled);
  useEffect(() => {
    closeDisabledRef.current = closeDisabled;
  });

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (!closeDisabledRef.current) {
          onCloseRef.current();
        }
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    // Initial focus: prefer the first focusable inside the body/footer (skip the
    // header close button), so forms with autoFocus inputs work as expected.
    queueMicrotask(() => {
      if (!modalRef.current) return;
      const scope =
        modalRef.current.querySelector<HTMLElement>(
          `.${styles.body}, .${styles.footer}`,
        ) ?? modalRef.current;
      const first = scope.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (first) {
        first.focus();
        return;
      }
      modalRef.current
        .querySelector<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        )
        ?.focus();
    });
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const heading = <h4 className={styles.title}>{title}</h4>;
  return createPortal(
    <div
      className={styles.backdrop}
      onClick={closeDisabled ? undefined : onClose}
    >
      <div
        ref={modalRef}
        className={width ? `${styles.modal} ${WIDTH_CLASS[width]}` : styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          {/* Without an icon the title is the header's own child, exactly as
              it has always been — the row wrapper appears only when there is
              something to sit beside it. */}
          {icon ? (
            <div className={styles.headMain}>
              <span
                className={`${styles.iconTile} ${ICON_TONE_CLASS[iconTone]}`}
                aria-hidden="true"
              >
                {icon}
              </span>
              {heading}
            </div>
          ) : (
            heading
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={onClose}
            disabled={closeDisabled}
            className={styles.close}
          >
            <X size={20} aria-hidden="true" />
          </Button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
